/**
 * Video KYC (VCIP) — Rudrik
 * ==========================
 * Mock mode: user uploads a single still-frame screenshot grabbed from a live
 * video session. Backend interprets via Claude Haiku 4.5 + heuristic baseline
 * and returns the RBI VCIP-compliant JSON contract.
 */
import React, { useRef, useState } from "react";
import axios from "axios";
import {
  Video, Upload, Loader2, ShieldCheck, AlertTriangle, X,
  CheckCircle2, XCircle, FileText, Camera, RefreshCw, UserCheck, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const API = `${window.location.origin}/api/vkyc`;

const VERDICT_COLOR = {
  PASS: "#22c55e", FAIL: "#dc2626",
  RETRY: "#f59e0b", MANUAL_ESCALATE: "#a855f7",
};
const RISK_COLOR = {
  LOW: "#22c55e", MEDIUM: "#f59e0b", HIGH: "#ef4444", CRITICAL: "#dc2626",
};

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function Pill({ ok, label, testId }) {
  const color = ok ? "#22c55e" : "#dc2626";
  return (
    <div data-testid={testId} style={{
      background: `${color}11`, border: `1px solid ${color}44`, borderRadius: 6,
      padding: "6px 10px", display: "flex", alignItems: "center", gap: 6, fontSize: 11,
    }}>
      {ok ? <CheckCircle2 size={11} style={{ color }} /> : <XCircle size={11} style={{ color }} />}
      <span style={{ color: "#cbd5e1", fontWeight: 600 }}>{label}</span>
    </div>
  );
}

export default function VKYCPage() {
  const [frame, setFrame] = useState(null);
  const [sessionType, setSessionType] = useState("AI_ASSISTED");
  const [expectedDocType, setExpectedDocType] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const pick = (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Frame must be under 8 MB"); return; }
    setFrame({ file, name: file.name, size: file.size, preview: URL.createObjectURL(file) });
  };

  const reset = () => {
    if (frame?.preview) URL.revokeObjectURL(frame.preview);
    setFrame(null);
    setResult(null);
  };

  const verify = async () => {
    if (!frame) { toast.error("Upload a still frame from the video session"); return; }
    setLoading(true); setResult(null);
    try {
      const frame_base64 = await readAsBase64(frame.file);
      const { data } = await axios.post(`${API}/verify`, {
        frame_base64,
        session_type: sessionType,
        expected_doc_type: expectedDocType || undefined,
      }, {
        withCredentials: true,
        timeout: 30000,
        headers: { "Content-Type": "application/json" },
      });
      setResult(data.result);
      const v = data.result?.overall_result;
      if (v === "PASS") toast.success("PASS — VCIP verified");
      else if (v === "FAIL") toast.error("FAIL — session rejected");
      else if (v === "RETRY") toast.warning("RETRY required");
      else toast.warning("Manual agent escalation");
    } catch (e) {
      const isTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      toast.error(isTimeout ? "Verification timed out — retry" : (e.response?.data?.detail || "Verification failed"));
    } finally { setLoading(false); }
  };

  const overall = result?.overall_result;
  const risk = result?.risk_level;
  const doc = result?.document_detected;
  const auth = result?.document_authenticity;
  const face = result?.face_in_video;
  const vcip = result?.rbi_vcip_checks;
  const live = face?.liveness_signals || {};

  return (
    <div data-testid="vkyc-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Video style={{ width: 26, height: 26, color: "#2563eb" }} />
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Video KYC (VCIP)</h1>
          <span data-testid="mock-badge" style={{ background: "rgba(245,158,11,0.15)", color: "#f59e0b", border: "1px solid #f59e0b55", fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>MOCK · RBI VCIP</span>
          <span style={{ background: "rgba(148,163,184,0.12)", color: "#cbd5e1", border: "1px solid #94a3b855", fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Session-only</span>
        </div>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 18 }}>
        Capture a still frame from the live video session (customer holding ID).
        Backend currently produces deterministic mock signals + Claude Haiku 4.5 verdict.
        Future vendor (HyperVerge / Gridlines / Digio) plugs in via the same endpoint contract.
      </p>

      {/* Upload + controls */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16, marginBottom: 18 }}>
        <div style={{
          background: "#0d1117", border: `1px dashed ${frame ? "#22c55e55" : "#1e2530"}`,
          borderRadius: 12, padding: 18, display: "flex", flexDirection: "column",
          alignItems: "center", gap: 12, minHeight: 240,
        }}>
          <div style={{ color: "#94a3b8", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Live Session Still Frame</div>
          {frame ? (
            <>
              <img src={frame.preview} alt="frame" style={{ maxWidth: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 8, border: "1px solid #1e2530" }} />
              <div style={{ color: "#cbd5e1", fontSize: 11, textAlign: "center" }}>
                {frame.name} <span style={{ color: "#64748b" }}>· {(frame.size / 1024).toFixed(1)} KB</span>
              </div>
              <button onClick={reset} data-testid="frame-clear" style={{ background: "transparent", border: "1px solid #1e2530", borderRadius: 6, color: "#94a3b8", padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <X size={11} /> Remove
              </button>
            </>
          ) : (
            <>
              <Camera size={36} style={{ color: "#475569" }} />
              <button onClick={() => inputRef.current?.click()} data-testid="frame-pick" style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: 6, color: "white", padding: "10px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Upload size={13} /> Choose still frame
              </button>
              <div style={{ color: "#64748b", fontSize: 10 }}>JPG / PNG · &lt; 8 MB · customer holding ID on camera</div>
            </>
          )}
          <input ref={inputRef} type="file" accept="image/*" onChange={(e) => pick(e.target.files?.[0])} style={{ display: "none" }} data-testid="frame-input" />
        </div>

        <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Session Type</div>
            <select value={sessionType} onChange={(e) => setSessionType(e.target.value)} data-testid="session-type" style={{ width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "9px 12px", color: "#f1f5f9", fontSize: 12 }}>
              <option value="AI_ASSISTED">AI-Assisted</option>
              <option value="LIVE_AGENT">Live Agent</option>
            </select>
          </div>
          <div>
            <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Expected Document (optional)</div>
            <select value={expectedDocType} onChange={(e) => setExpectedDocType(e.target.value)} data-testid="expected-doc-type" style={{ width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "9px 12px", color: "#f1f5f9", fontSize: 12 }}>
              <option value="">— Auto-detect —</option>
              <option value="AADHAAR">Aadhaar</option>
              <option value="PAN">PAN</option>
              <option value="PASSPORT">Passport</option>
              <option value="DL">Driving Licence</option>
              <option value="VOTER_ID">Voter ID</option>
            </select>
          </div>
          <button
            onClick={verify}
            disabled={loading || !frame}
            data-testid="verify-btn"
            style={{
              marginTop: "auto",
              background: loading || !frame ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
              border: "none", color: "white", padding: "11px 20px", borderRadius: 6,
              fontSize: 13, fontWeight: 700, cursor: loading || !frame ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            {loading ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} />}
            {loading ? "Analyzing frame…" : "Run VCIP Verification"}
          </button>
          {(frame || result) && (
            <button onClick={reset} data-testid="reset-btn" style={{ background: "transparent", border: "1px solid #1e2530", borderRadius: 6, color: "#94a3b8", padding: "8px 14px", fontSize: 11, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <RefreshCw size={11} /> Reset
            </button>
          )}
        </div>
      </div>

      {result && (
        <div data-testid="vkyc-result-card" style={{ background: "#0d1117", border: `1px solid ${VERDICT_COLOR[overall] || "#1e2530"}`, borderRadius: 12, padding: 22 }}>
          {/* Verdict header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            {overall === "PASS"
              ? <CheckCircle2 size={22} style={{ color: "#22c55e" }} />
              : overall === "FAIL"
                ? <XCircle size={22} style={{ color: "#dc2626" }} />
                : <AlertTriangle size={22} style={{ color: "#f59e0b" }} />}
            <div style={{ flex: 1 }}>
              <div data-testid="overall-result" style={{ color: VERDICT_COLOR[overall] || "#f1f5f9", fontSize: 20, fontWeight: 800 }}>{overall || "—"}</div>
              <div style={{ color: "#94a3b8", fontSize: 12 }}>{result?.compliance_statement}</div>
            </div>
            <div style={{ background: "#080c12", border: `1px solid ${RISK_COLOR[risk]}55`, borderRadius: 8, padding: "10px 14px", minWidth: 90 }}>
              <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Risk</div>
              <div data-testid="risk-level" style={{ color: RISK_COLOR[risk], fontSize: 16, fontWeight: 800 }}>{risk}</div>
            </div>
            <div style={{ background: "#080c12", border: `1px solid ${VERDICT_COLOR[overall]}55`, borderRadius: 8, padding: "10px 14px", minWidth: 130 }}>
              <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Action</div>
              <div data-testid="recommended-action" style={{ color: VERDICT_COLOR[overall], fontSize: 13, fontWeight: 800 }}>{result?.recommended_action}</div>
            </div>
          </div>

          {/* Retry / fail messages */}
          {result?.retry_instruction && (
            <div style={{ background: "rgba(245,158,11,0.08)", border: "1px solid #f59e0b55", borderRadius: 6, padding: 12, marginBottom: 14, display: "flex", gap: 8, alignItems: "flex-start" }}>
              <RefreshCw size={13} style={{ color: "#f59e0b", marginTop: 2 }} />
              <div style={{ color: "#fbbf24", fontSize: 12 }}>{result.retry_instruction}</div>
            </div>
          )}
          {result?.fail_reason && (
            <div style={{ background: "rgba(220,38,38,0.08)", border: "1px solid #dc262655", borderRadius: 6, padding: 12, marginBottom: 14, display: "flex", gap: 8, alignItems: "flex-start" }}>
              <XCircle size={13} style={{ color: "#dc2626", marginTop: 2 }} />
              <div style={{ color: "#fca5a5", fontSize: 12 }}>{result.fail_reason}</div>
            </div>
          )}

          {/* Sections */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <section style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: 12 }}>
              <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                <FileText size={11} /> Document Detected
              </div>
              <div style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{doc?.type || "—"}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                <Pill ok={doc?.found} label="Document found" testId="doc-found" />
                <Pill ok={doc?.clearly_visible} label="Clearly visible" testId="doc-clear" />
                <Pill ok={doc?.id_number_readable} label="ID number readable" testId="doc-id-readable" />
                <Pill ok={doc?.photo_on_doc_visible} label="Photo on doc visible" testId="doc-photo-visible" />
              </div>
              {result?.extracted_data?.id_number_masked && (
                <div style={{ color: "#94a3b8", fontSize: 11, fontFamily: "ui-monospace, monospace", marginTop: 10 }}>
                  ID: {result.extracted_data.id_number_masked}
                </div>
              )}
            </section>

            <section style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: 12 }}>
              <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                <ShieldCheck size={11} /> Document Authenticity
              </div>
              <div data-testid="auth-verdict" style={{ color: auth?.is_genuine ? "#22c55e" : "#dc2626", fontSize: 14, fontWeight: 800, marginBottom: 4 }}>
                {auth?.verdict || "—"}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                <Pill ok={auth?.is_genuine} label="Is genuine" testId="auth-genuine" />
                <Pill ok={!auth?.printed_from_screen} label="Not from screen" testId="auth-not-screen" />
                <Pill ok={!(auth?.tamper_signals?.length > 0)} label="No tamper signals" testId="auth-no-tamper" />
              </div>
              {auth?.tamper_signals?.length > 0 && (
                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {auth.tamper_signals.map((s, i) => (
                    <span key={`${s}-${i}`} data-testid={`tamper-${i}`} style={{ background: "#dc262622", color: "#fca5a5", border: "1px solid #dc262655", padding: "2px 6px", borderRadius: 3, fontSize: 10, fontFamily: "ui-monospace, monospace" }}>{s}</span>
                  ))}
                </div>
              )}
            </section>

            <section style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: 12 }}>
              <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                <UserCheck size={11} /> Face in Video
              </div>
              <div data-testid="face-confidence" style={{ color: (face?.confidence ?? 0) >= 90 ? "#22c55e" : (face?.confidence ?? 0) >= 75 ? "#f59e0b" : "#dc2626", fontSize: 22, fontWeight: 800, lineHeight: 1 }}>
                {face?.confidence ?? 0}%
              </div>
              <div style={{ color: "#94a3b8", fontSize: 10, marginBottom: 8, marginTop: 2 }}>Match confidence</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <Pill ok={face?.matches_document_photo} label="Matches doc photo" testId="face-match-doc" />
                <Pill ok={live?.person_speaking} label="Person speaking" testId="liveness-speaking" />
                <Pill ok={live?.natural_movement} label="Natural movement" testId="liveness-movement" />
                <Pill ok={live?.not_frozen_image} label="Not frozen image" testId="liveness-not-frozen" />
              </div>
            </section>

            <section style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: 12 }}>
              <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>RBI VCIP Checks</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <Pill ok={vcip?.live_interaction_confirmed} label="Live interaction" testId="vcip-live" />
                <Pill ok={vcip?.id_clearly_shown} label="ID clearly shown" testId="vcip-id" />
                <Pill ok={vcip?.face_matches_id} label="Face matches ID" testId="vcip-face" />
                <Pill ok={vcip?.consent_given} label="Consent captured" testId="vcip-consent" />
                <Pill ok={vcip?.india_location_geotag} label="India geolocation" testId="vcip-geo" />
                <Pill ok={vcip?.session_recorded} label="Session recorded" testId="vcip-recorded" />
              </div>
            </section>
          </div>

          {/* Risk flags */}
          {result?.risk_flags?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Risk Flags</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {result.risk_flags.map((f, i) => {
                  const sev = (f.severity || "MEDIUM").toUpperCase();
                  const c = RISK_COLOR[sev] || "#f59e0b";
                  return (
                    <div key={`${f.flag}-${i}`} data-testid={`risk-flag-${i}`} style={{ background: `${c}10`, border: `1px solid ${c}44`, borderRadius: 6, padding: "8px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <AlertTriangle size={12} style={{ color: c }} />
                        <span style={{ color: c, fontSize: 12, fontWeight: 700 }}>{f.flag}</span>
                        <span style={{ marginLeft: "auto", color: c, fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{sev}</span>
                      </div>
                      {f.action && <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 2, marginLeft: 20 }}>→ {f.action}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", color: "#64748b", fontSize: 10, marginTop: 8, paddingTop: 8, borderTop: "1px solid #1e2530" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Sparkles size={9} /> {result.ai_mode || "—"} · {result.source} · {result.session_type}
            </span>
            <span>Cost ₹{result.cost_inr?.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
